import { EMPTY, concat, of, merge, from } from 'rxjs';
import { catchError, mergeMap, takeWhile, tap } from 'rxjs/operators';

import { startBulkRatingGetMessages } from './bulk-rate.observables';
import * as Actions from './csw.actions';
import * as Api from './csw.api';
import { getErrorMessagesFromSaveConfigAndRateResponse } from './csw.error-helpers';
import * as Builders from './csw.request-builders';
import { messages } from './messages';
import { compareRates, compareInsuranceRates } from './rate-recommendation.observables';
import { watchForRateShopperPromo } from './rate-shopper-promo.observables';
import { SAVE_CONFIG_AND_RATE, PARCEL_OVERSIZE_OVERWEIGHT_ERROR_SHOWN } from '../../constants/analytics-events';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { RETURNS } from '../../enums/csw-types';
import { rateRefreshSubject, createLabelSubject, serviceCollectionSubject } from '../../fulfillment-planner/context';
import * as EntityActions from '../../store/entities/entities.actions';
import { ShipmentConfigurationService } from '../../services/shipping-configuration-service';
import { bulkRateFulfillmentPlans } from '../bulk/bulk.api';
import Global from '../global-selectors';
import { pusherMessageToRateRefreshSubject } from '../../fulfillment-planner/helpers/pusher-update-rates-mapper';
import { calculateDifferences } from '../../fulfillment-planner/payload-diff/core';
import { entityWhitelistKeys, entityFalsyChecksOk } from '../../fulfillment-planner/payload-diff/entity';
import { externalFalsyChecksOk, externalWhitelistKeys } from '../../fulfillment-planner/payload-diff/external';
import { rateBrowserFalsyChecksOk, rateBrowserWhitelistKeys } from '../../fulfillment-planner/payload-diff/rate-browser';
import { returnsFalsyChecksOk, returnsWhitelistKeys } from '../../fulfillment-planner/payload-diff/returns';
import { getActualSalesOrderIds } from '../orders/orders.helpers';
import { LogService } from '../../services/log-service';
import { Segment } from '../../services/analytics-services/segment';

const OVERSIZE_OVERWEIGHT_ERROR_CODES = ['InvalidWeight', 'InvalidDimensions'];

const getRateDiffObservable = (contextPayload, reduxPayload, cswTypeId, enableCswRatePayloadDiff) => {
  if (!enableCswRatePayloadDiff) {
    return EMPTY;
  }
  if (cswTypeId === RETURNS) {
    return calculateDifferences(contextPayload, reduxPayload, returnsWhitelistKeys, returnsFalsyChecksOk, cswTypeId);
  }
  return calculateDifferences(contextPayload, reduxPayload, rateBrowserWhitelistKeys, rateBrowserFalsyChecksOk, cswTypeId);
};

export const getRateFromFulfillmentPlan = ({
  action$,
  getState,
  fulfillmentPlanId,
  onSuccess,
  onError,
  shippingConfiguration,
  cswTypeId,
  enableCswRatePayloadDiff,
}) => {
  const state = getState();

  const requestData = ShipmentConfigurationService.mapModelToRequest({
    shippingConfiguration: Builders.buildSaveShippingConfigurationAndRateRequest(state, {
      fulfillmentPlanId,
    }),
  });

  const diffAndLog$ = getRateDiffObservable(shippingConfiguration, requestData, cswTypeId, enableCswRatePayloadDiff);
  return merge(
    compareRates(requestData, action$, getState),
    compareInsuranceRates(requestData, action$, getState),
    Api.getRateFromFulfillmentPlan(requestData).pipe(
      tap(() => createLabelSubject.next({ fulfillmentPlanId, errorMessages: [] })),
      mergeMap((response) => {
        const rateError = response?.rateError;
        const message = response && response.data ? getErrorMessagesFromSaveConfigAndRateResponse(response.data) : [];

        const { carrierId, confirmationId, packageTypeId, sellerProviderId, shippingServiceId, serviceCollectionGuid, fulfillmentPlans } =
          response || {};

        const packages = fulfillmentPlans?.find((f) => f.fulfillmentPlanId === fulfillmentPlanId)?.labelConfiguration?.packages;

        rateRefreshSubject.next({
          fulfillmentPlanId,
          isRefreshingRate: false,
          errors: message,
          rateObject: {
            ...response,
          },
        });
        serviceCollectionSubject.next({
          fulfillmentPlanId,
          carrierId,
          confirmationId: confirmationId?.toString(),
          packageTypeId: packageTypeId?.toString(),
          sellerLabelProviderId: sellerProviderId?.toString(),
          serviceId: shippingServiceId?.toString(),
          serviceCollectionGuid,
          packages,
        });
        if (rateError) {
          return concat(
            of(Actions.updateFulfillmentPlanAndRateFailure(fulfillmentPlanId, requestData.isReturn ? emptyArray : [message])),
            onSuccess(),
          );
        }

        const normalizedResponse = {
          fulfillmentPlanId,
          updatedProperties: emptyObject,
          rateError: null,
          rate: {
            ...response,
          },
        };
        return concat(of(Actions.updateFulfillmentPlanAndRateSuccess(fulfillmentPlanId, normalizedResponse)), onSuccess(), diffAndLog$);
      }),
      catchError(({ response }) => {
        const errorMessages = response && response.data ? getErrorMessagesFromSaveConfigAndRateResponse(response.data) : emptyArray;
        rateRefreshSubject.next({
          fulfillmentPlanId,
          isRefreshingRate: false,
          errors: errorMessages,
        });
        return concat(
          of(Actions.updateFulfillmentPlanAndRateFailure(fulfillmentPlanId, errorMessages)),
          onError(errorMessages),
          diffAndLog$,
        );
      }),
    ),
  );
};

export const saveExternalFulfillmentConfigAndRate = ({
  getState,
  fulfillmentPlanId,
  onSuccess,
  onError,
  shippingConfiguration,
  cswTypeId,
  enableCswRatePayloadDiff,
}) => {
  rateRefreshSubject.next({
    fulfillmentPlanId,
    isRefreshingRate: true,
  });
  const state = getState();
  const salesOrderIds = getActualSalesOrderIds(state, [fulfillmentPlanId]);
  const payload = Builders.buildExternalFulfillmentSaveAndRateRequest(state, {
    fulfillmentPlanId,
  });

  const diffAndLog$ = enableCswRatePayloadDiff
    ? calculateDifferences(shippingConfiguration, payload, externalWhitelistKeys, externalFalsyChecksOk, cswTypeId)
    : EMPTY;
  return Api.externalFulfillmentSaveAndRate(fulfillmentPlanId, payload).pipe(
    tap(() => createLabelSubject.next({ fulfillmentPlanId, errorMessages: [] })),
    mergeMap((response) => {
      const rateError = response?.rateError;
      rateRefreshSubject.next({
        fulfillmentPlanId,
        isRefreshingRate: false,
        errors: rateError,
        rateObject: response?.rate,
      });

      const updateAction = rateError
        ? Actions.externalFulfillmentSaveAndRateFailure(fulfillmentPlanId, [rateError])
        : Actions.externalFulfillmentSaveAndRateSuccess(fulfillmentPlanId, response);

      return concat(onSuccess({ updateAction }), diffAndLog$);
    }),
    catchError((errorResponse) => {
      const errorMessages = (errorResponse?.response?.data?.Errors ?? emptyArray).map((error) => error.Message);
      rateRefreshSubject.next({
        fulfillmentPlanId,
        isRefreshingRate: false,
        errors: errorMessages,
      });
      return concat(
        of(Actions.externalFulfillmentSaveAndRateFailure(fulfillmentPlanId, errorMessages)),
        of(EntityActions.bulkRequestEntities({ salesOrderIds })),
        onError(errorMessages),
        diffAndLog$,
      );
    }),
  );
};

export const saveConfigAndRateAsync = ({
  getState,
  fulfillmentPlanId,
  onSuccess,
  onError,
  shippingConfiguration,
  cswTypeId,
  enableCswRatePayloadDiff,
  shouldSkipSave,
}) => {
  const state = getState();
  const shippingConfig = Builders.buildSaveShippingConfigurationAndRateRequest(state, { fulfillmentPlanId });

  const diffAndLog$ = enableCswRatePayloadDiff
    ? calculateDifferences(shippingConfiguration, shippingConfig, entityWhitelistKeys, entityFalsyChecksOk, cswTypeId)
    : EMPTY;

  const rateTimingStartMs = Date.now();

  // don't save the configuration just because a user opened an order, only fetch new rate
  const api$ = shouldSkipSave ? of(null) : Api.updateFulfillmentPlanConfig(fulfillmentPlanId, shippingConfig);

  return api$.pipe(
    mergeMap(() => {
      const logContext = {
        fulfillmentPlanId,
        operation: 'bulk-rating',
      };

      const onRatingError = ({ isTimeout }) => {
        if (isTimeout) {
          rateRefreshSubject.next({
            timestamp: Date.now(),
            fulfillmentPlanId,
            isRefreshingRate: false,
            errors: [messages.rateCalculationTimeout],
          });
        }

        return EMPTY;
      };

      return startBulkRatingGetMessages({
        apiCallback: () => from(bulkRateFulfillmentPlans({ fulfillmentPlanIds: [fulfillmentPlanId], forceRefreshRate: true })),
        pusherTimeoutMs: 180000,
        onError: onRatingError,
        logContext,
        fulfillmentPlanId,
      }).pipe(
        tap(() => createLabelSubject.next({ fulfillmentPlanId, errorMessages: [] })),
        mergeMap(({ rateJobGuids, findMessage$ }) => {
          rateRefreshSubject.next({
            timestamp: Date.now(),
            fulfillmentPlanId,
            isRefreshingRate: true,
            errors: [],
            rateObject: null,
          });

          if (!rateJobGuids?.length) {
            return EMPTY;
          }

          return merge(
            watchForRateShopperPromo(fulfillmentPlanId, shippingConfig.serviceCollectionGuid, rateJobGuids[0], rateRefreshSubject),
            findMessage$.pipe(
              takeWhile(({ pusherData }) => !pusherData?.final, true),
              mergeMap(({ pusherData }) => {
                const orderData = pusherData.Orders?.find((order) => order.OrderID?.toString() === fulfillmentPlanId.toString());

                if (!orderData) {
                  rateRefreshSubject.next({
                    timestamp: Date.now(),
                    fulfillmentPlanId,
                    isRefreshingRate: false,
                    errors: [messages.noRateDataFound],
                  });
                  return EMPTY;
                }

                const code = Global.selectSellerDefaultCurrency(getState());
                const rateObject = pusherMessageToRateRefreshSubject(orderData, code);

                const errorMessages = getErrorMessagesFromSaveConfigAndRateResponse(orderData);
                const hasRateError = orderData?.RateError;

                const errors = hasRateError ? [orderData.RateError] : errorMessages;

                const rateTimingEndMs = Date.now();
                const rateTimingDurationMs = rateTimingEndMs - rateTimingStartMs;

                const sellerId = Global.selectSellerSellerId(getState());
                Segment.trackEvent(SAVE_CONFIG_AND_RATE, {
                  fulfillmentPlanId,
                  durationMs: rateTimingDurationMs,
                  hasRateError,
                  errors,
                  rateWarnings: rateObject.rateWarnings,
                  sellerId,
                });

                const rawOrderErrors = orderData?.Errors ?? [];
                const oversizeOrOverweightCode = OVERSIZE_OVERWEIGHT_ERROR_CODES.find(
                  (errorCode) => orderData?.RateError === errorCode || rawOrderErrors.some((e) => e.Code === errorCode),
                );
                if (oversizeOrOverweightCode) {
                  Segment.trackEvent(PARCEL_OVERSIZE_OVERWEIGHT_ERROR_SHOWN, {
                    sellerId,
                    fulfillmentPlanId,
                    errorCode: oversizeOrOverweightCode,
                  });
                }

                rateRefreshSubject.next({
                  timestamp: Date.now(),
                  fulfillmentPlanId,
                  isRefreshingRate: false,
                  errors,
                  rateObject: hasRateError ? null : rateObject,
                  rateJobGuid: rateJobGuids[0],
                });

                const rateResponse = {
                  fulfillmentPlanId,
                  updatedProperties: emptyObject,
                  rateError: orderData?.RateError || null,
                  rate: {
                    shippingCost: rateObject.shippingCost,
                    insuranceCost: rateObject.insuranceCost,
                    confirmationCost: rateObject.confirmationCost,
                    otherCost: rateObject.otherCost,
                    totalCost: rateObject.totalCost,
                    estimatedDelivery: rateObject.estimatedDelivery,
                    errors: rateObject.errors,
                    rateWarnings: rateObject.rateWarnings,
                  },
                };

                const rateUpdateAction = hasRateError
                  ? Actions.updateFulfillmentPlanAndRateFailure(fulfillmentPlanId, [orderData.RateError])
                  : Actions.updateFulfillmentPlanAndRateSuccess(fulfillmentPlanId, rateResponse);

                return concat(
                  onSuccess({
                    updateAction: rateUpdateAction,
                    shouldSkipBulkLoad: false,
                  }),
                  diffAndLog$,
                );
              }),
              catchError((error) => {
                LogService.logWarning('Failed to receive bulk rating pusher message', { error, fulfillmentPlanId });

                rateRefreshSubject.next({
                  timestamp: Date.now(),
                  fulfillmentPlanId,
                  isRefreshingRate: false,
                  errors: [messages.rateCalculationTimeout],
                });

                return EMPTY;
              }),
            ),
          );
        }),
        catchError((error) => {
          const message = error?.response?.data?.message ? [error.response.data.message] : [messages.failedToInitiateRating];

          rateRefreshSubject.next({
            timestamp: Date.now(),
            fulfillmentPlanId,
            isRefreshingRate: false,
            errors: message,
          });

          const updateAction = Actions.updateFulfillmentPlanAndRateFailure(fulfillmentPlanId, message);
          return onError({ updateAction, errorMessage: message }, diffAndLog$);
        }),
      );
    }),
    catchError((error) => {
      const message = error?.response?.data?.message ? [error.response.data.message] : [messages.failedToSaveConfiguration];

      rateRefreshSubject.next({
        timestamp: Date.now(),
        fulfillmentPlanId,
        isRefreshingRate: false,
        errors: message,
      });

      const updateAction = Actions.updateFulfillmentPlanAndRateFailure(fulfillmentPlanId, message);
      return onError({ updateAction, errorMessage: message }, diffAndLog$);
    }),
  );
};
